/**
 * Blog v2 bodies sometimes store a product as raw markdown:
 *   ![Product](image-url)
 *   [Shop Name](shop-url)
 * Turn that into a photo plus a real shop link. Leave ordinary posts alone.
 */
import React from "react";
import { useEdit } from "./InlineEdit";

export type BlogBodyPart =
  | { type: "text"; text: string; strong?: boolean; em?: boolean }
  | { type: "link"; text: string; href: string };

export type BlogBodyParagraph = { type: "paragraph"; parts: BlogBodyPart[] };
export type BlogBodyList = { type: "list"; items: string[] };
export type BlogBodyCopy = BlogBodyParagraph | BlogBodyList;

export type BlogBodyBlock =
  | BlogBodyCopy
  | { type: "heading"; text: string }
  | {
      type: "figure";
      image: string;
      caption: string;
      /** Scene and comparison photos span the article. */
      span: "full";
    }
  | {
      type: "content";
      image: string;
      title: string;
      href: string;
      caption: string;
    }
  | {
      type: "product";
      /** 1-based look number. Hidden when this is the only product. */
      index: number;
      /** False for a single product — no "01" and no "1." in the title. */
      numbered: boolean;
      /** Side column for a product shot; full width when the photo is the point. */
      layout: "side" | "full";
      image: string;
      alt: string;
      caption: string;
      label: string;
      href: string;
      /** Right-column heading, e.g. "1. The flat that…". */
      title: string;
      /** Name under the photo, without the leading number. */
      name: string;
      body: BlogBodyCopy[];
    };

const IMAGE_LINE =
  /^!\[([^\]]*)\]\(\s*<?([^)\s>]+)>?(?:\s+(?:"[^"]*"|'[^']*'))?\s*\)\s*$/;
const LINK_LINE =
  /^\[([^\]]+)\]\(\s*<?([^)\s>]+)>?(?:\s+(?:"[^"]*"|'[^']*'))?\s*\)\s*$/;
const HEADING_LINE = /^#{1,6}\s+(.+)$/;
const BULLET_LINE = /^[-*]\s+(.+)$/;
const INLINE_LINK = /\[([^\]]+)\]\(\s*<?(https?:\/\/[^)\s>]+)>?\s*\)/g;

function isLaidOutBlock(block: BlogBodyBlock): boolean {
  return block.type === "product" || block.type === "figure" || block.type === "content";
}

/** Only show a caption when there is real text (ignore whitespace / placeholder alts). */
function displayCaption(raw: string | null | undefined): string {
  const text = String(raw || "").trim();
  if (!text) return "";
  if (/^(content|product|image|photo|screenshot|wide|full)\s*:?\s*$/i.test(text)) {
    return "";
  }
  return text;
}

/** Drop generator placeholders like "Untitled" so cards don't show a fake title bar. */
export function displayContentTitle(raw: string | null | undefined): string {
  const text = String(raw || "").trim();
  if (!text) return "";
  if (/^(untitled|content|title|new post)$/i.test(text)) return "";
  if (/^content\s*:/i.test(text)) {
    return text.replace(/^content\s*:\s*/i, "").trim();
  }
  return text;
}

function contentCardHtml(block: Extract<BlogBodyBlock, { type: "content" }>): string {
  const title = displayContentTitle(block.title);
  const caption = displayCaption(block.caption);
  const img = block.image
    ? `<div class="mte-tpl-post-content-media"><img src="${escapeHtml(block.image)}" alt="${escapeHtml(title || "Content")}" /></div>`
    : "";
  const titleHtml = title
    ? `<span class="mte-tpl-post-content-title">${escapeHtml(title)}</span>`
    : "";
  const cap = caption
    ? `<p class="mte-tpl-post-content-caption">${escapeHtml(caption)}</p>`
    : "";
  const meta =
    titleHtml || cap
      ? `<div class="mte-tpl-post-content-body">${titleHtml}${cap}</div>`
      : "";
  const inner = `${img}${meta}`;
  const body = block.href
    ? `<a href="${escapeHtml(block.href)}" target="_blank" rel="noopener noreferrer">${inner}</a>`
    : inner;
  return `<article class="mte-tpl-post-content${meta ? "" : " mte-tpl-post-content--media-only"}">${body}</article>`;
}

function contentGalleryHtml(
  items: Extract<BlogBodyBlock, { type: "content" }>[],
): string {
  if (!items.length) return "";
  const cards = items.map((item) => contentCardHtml(item));
  return `<div class="mte-tpl-post-content-gallery" data-count="${items.length}">${cards.join("")}</div>`;
}

function figureCardHtml(block: Extract<BlogBodyBlock, { type: "figure" }>): string {
  const caption = displayCaption(block.caption);
  const cap = caption ? `<figcaption>${escapeHtml(caption)}</figcaption>` : "";
  return `<figure class="mte-tpl-post-figure mte-tpl-post-figure--grid"><img src="${escapeHtml(block.image)}" alt="${escapeHtml(caption)}" />${cap}</figure>`;
}

function figureGalleryHtml(
  items: Extract<BlogBodyBlock, { type: "figure" }>[],
): string {
  if (!items.length) return "";
  if (items.length === 1) {
    const caption = displayCaption(items[0].caption);
    const cap = caption ? `<figcaption>${escapeHtml(caption)}</figcaption>` : "";
    return `<figure class="mte-tpl-post-figure mte-tpl-post-figure--full"><img src="${escapeHtml(items[0].image)}" alt="${escapeHtml(caption)}" />${cap}</figure>`;
  }
  const cards = items.map((item) => figureCardHtml(item));
  return `<div class="mte-tpl-post-figure-gallery mte-tpl-post-content-gallery" data-count="${items.length}">${cards.join("")}</div>`;
}

function looksLikeHtml(body: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(body);
}

/** http(s) only. Placeholder hosts (example.com) are not real product photos. */
export function usableProductUrl(raw: string): string {
  const t = String(raw || "").trim();
  if (!/^https?:\/\//i.test(t)) return "";
  if (/\.(mp4|mov|webm|m3u8|m4v)(\?|#|$)/i.test(t)) return "";
  try {
    const host = new URL(t).hostname.replace(/^www\./, "").toLowerCase();
    if (
      host === "example.com" ||
      host === "example.org" ||
      host === "example.net" ||
      host.endsWith(".example.com")
    ) {
      return "";
    }
    return t;
  } catch {
    return "";
  }
}

function escapeHtml(s: string): string {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function splitInline(text: string): BlogBodyPart[] {
  const parts: BlogBodyPart[] = [];
  const re = new RegExp(INLINE_LINK.source, "g");
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text))) {
    if (match.index > last) {
      parts.push({ type: "text", text: text.slice(last, match.index) });
    }
    const href = usableProductUrl(match[2]);
    if (href) {
      parts.push({ type: "link", text: match[1], href });
    } else {
      parts.push({ type: "text", text: match[0] });
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ type: "text", text: text.slice(last) });
  return withEmphasis(parts).filter((part) => part.type === "link" || part.text.length > 0);
}

function withEmphasis(parts: BlogBodyPart[]): BlogBodyPart[] {
  const out: BlogBodyPart[] = [];
  const re = /(\*\*|__)([\s\S]+?)\1|\*([^*\n]+)\*|_([^_\n]+)_/g;
  for (const part of parts) {
    if (part.type !== "text" || part.strong || part.em) {
      out.push(part);
      continue;
    }
    const text = part.text;
    let last = 0;
    re.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(text))) {
      if (match.index > last) {
        out.push({ type: "text", text: text.slice(last, match.index) });
      }
      const inner = match[2] || match[3] || match[4] || "";
      if (match[2]) out.push({ type: "text", text: inner, strong: true });
      else out.push({ type: "text", text: inner, em: true });
      last = match.index + match[0].length;
    }
    if (last === 0) out.push(part);
    else if (last < text.length) out.push({ type: "text", text: text.slice(last) });
  }
  return out;
}

/** Strip generator prefixes like `product: Name` from alt / labels. */
export function cleanProductLabel(raw: string | null | undefined): string {
  const alt = String(raw || "").trim();
  if (!alt) return "";
  return alt
    .replace(/^(?:product|col|column|content|image|photo|screenshot)\s*:\s*/i, "")
    .replace(/^shop\s+/i, "")
    .trim();
}

function productAlt(rawAlt: string, heading: string): string {
  const cleaned = cleanProductLabel(rawAlt);
  const generic = !cleaned || /^(product|image|photo)$/i.test(cleaned);
  const fromHeading = cleanProductLabel(heading.replace(/^\d+\.\s*/, ""));
  if (generic && fromHeading) return fromHeading;
  return cleaned || fromHeading || "Product";
}

function parseImageRole(rawAlt: string): {
  role: "wide" | "product" | "auto";
  caption: string;
} {
  const alt = rawAlt.trim();
  const wide = alt.match(/^(?:wide|full)\s*:\s*(.*)$/i);
  if (wide) return { role: "wide", caption: wide[1].trim() };
  const product = alt.match(/^(?:product|col|column)\s*:\s*(.*)$/i);
  if (product) return { role: "product", caption: product[1].trim() };
  if (/^(product|image|photo|screenshot)$/i.test(alt)) {
    return { role: "auto", caption: "" };
  }
  return { role: "auto", caption: alt };
}

/** Roundup / podcast / social links should be content cards, not shop products. */
function looksLikeContentCard(
  alt: string,
  label: string,
  href: string,
): boolean {
  if (/^content\s*:/i.test(alt) || /^content\s*:/i.test(label)) return true;
  if (/^product\s*:/i.test(alt) || /^shop\s+/i.test(label)) return false;
  if (/^(listen|watch|read|view|open)\b/i.test(label.trim())) return true;
  try {
    const host = new URL(href).hostname.replace(/^www\./, "").toLowerCase();
    return /(^|\.)(spotify\.com|youtube\.com|youtu\.be|instagram\.com|tiktok\.com|podcasts\.apple\.com|apple\.com|art19\.com|soundcloud\.com|anchor\.fm|transistor\.fm|deezer\.com)$/i.test(
      host,
    );
  } catch {
    return false;
  }
}

function readCaptionLine(line: string): string {
  const t = line.trim();
  const wrapped =
    t.match(/^_(.+)_$/) || t.match(/^\*(.+)\*$/) || t.match(/^\((.+)\)$/);
  if (!wrapped) return "";
  return wrapped[1].replace(/^_|_$/g, "").replace(/^\(|\)$/g, "").trim();
}

/** Drop **bold** / _italic_ markers so titles are not shown with the syntax. */
function plainInlineMarkdown(text: string): string {
  return String(text || "")
    .replace(/(\*\*|__)([\s\S]+?)\1/g, "$2")
    .replace(/(^|[^\w])\*([^*\n]+)\*(?=[^\w]|$)/g, "$1$2")
    .replace(/(^|[^\w])_([^_\n]+)_(?=[^\w]|$)/g, "$1$2")
    .replace(/\s+/g, " ")
    .trim();
}

/** Explicit `product: Name` / non-generic alt wins over an absorbed look heading. */
function explicitProductName(alt: string, label: string): string {
  const fromAlt = cleanProductLabel(alt);
  if (fromAlt && !/^(product|image|photo)$/i.test(fromAlt)) return fromAlt;
  const fromLabel = String(label || "")
    .replace(/^shop\s+/i, "")
    .trim();
  // Shop labels are often store names — only use when we have nothing else.
  if (fromLabel && !/^(shop|buy|link)$/i.test(fromLabel)) return "";
  return "";
}

/** AI look headings: "1. The flat…" or "Look — Product Name". */
function isLookStyleHeading(text: string): boolean {
  const t = plainInlineMarkdown(text);
  if (!t) return false;
  if (/^\d+\.\s+\S/.test(t)) return true;
  if (/\s+[-–—]\s+\S/.test(t)) return true;
  return false;
}

function lookName(title: string, alt: string, label: string): string {
  const explicit = explicitProductName(alt, label);
  if (explicit) return explicit;
  const fromTitle = plainInlineMarkdown(title.replace(/^\d+\.\s*/, ""));
  const pieces = fromTitle.split(/\s+[-–—]\s+/);
  const product = pieces.length > 1 ? pieces[pieces.length - 1].trim() : fromTitle;
  if (product) return product;
  const fromLabel = label.replace(/^shop\s+/i, "").trim();
  return fromLabel || cleanProductLabel(alt) || "Product";
}

/**
 * Pull the heading and the paragraphs under it into the product row,
 * matching a look: photo on the left, write-up and shop link on the right.
 *
 * Do not absorb a plain page-header / section title into a product that already
 * has an explicit `product: Name` — that made custom products "take" the title.
 */
function foldProductLooks(blocks: BlogBodyBlock[]): BlogBodyBlock[] {
  const out: BlogBodyBlock[] = [];
  let lookIndex = 0;
  for (const block of blocks) {
    if (block.type !== "product") {
      out.push(block);
      continue;
    }
    let headingAt = -1;
    for (let k = out.length - 1; k >= 0; k--) {
      if (out[k].type === "paragraph" || out[k].type === "list") continue;
      if (out[k].type === "heading") headingAt = k;
      break;
    }
    let title = block.title;
    let body = block.body;
    const hasExplicitName = Boolean(explicitProductName(block.alt, block.label));
    if (headingAt >= 0) {
      const heading = out[headingAt];
      const headingText = heading.type === "heading" ? heading.text : "";
      const shouldFold =
        Boolean(headingText) &&
        (!hasExplicitName || isLookStyleHeading(headingText));
      if (shouldFold && heading.type === "heading") {
        title = heading.text;
        body = out.slice(headingAt + 1).filter(
          (item): item is BlogBodyCopy =>
            item.type === "paragraph" || item.type === "list",
        );
        out.splice(headingAt);
      }
    } else {
      const prev = out[out.length - 1];
      if (prev && (prev.type === "paragraph" || prev.type === "list")) {
        body = [prev];
        out.pop();
      }
    }
    lookIndex += 1;
    const name = lookName(title, block.alt, block.label);
    out.push({
      ...block,
      index: lookIndex,
      numbered: false,
      title: title || name,
      name,
      body,
    });
  }
  const productCount = out.filter((block) => block.type === "product").length;
  if (productCount <= 1) {
    return out.map((block) => {
      if (block.type !== "product") return block;
      const name = lookName(block.title, block.alt, block.label);
      const lookTitle = block.title.trim();
      // Keep a distinct look heading (e.g. "1. The flat…") even for one product.
      const title =
        lookTitle &&
        lookTitle !== name &&
        lookTitle.replace(/^\d+\.\s*/, "").trim() !== name
          ? lookTitle
          : name;
      return preferProductName({ ...block, numbered: false, title, name });
    });
  }
  return out.map((block) => {
    if (block.type !== "product") return block;
    const name = lookName(block.title, block.alt, block.label);
    const title = /^\d+\.\s+/.test(block.title)
      ? block.title
      : `${block.index}. ${name}`;
    return preferProductName({ ...block, numbered: true, title, name });
  });
}

/** A store name like "Nordstrom" is not the product. The bold lead-in is. */
function preferProductName(
  block: Extract<BlogBodyBlock, { type: "product" }>,
): Extract<BlogBodyBlock, { type: "product" }> {
  const first = block.body.find((item) => item.type === "paragraph");
  if (!first || first.type !== "paragraph") return block;
  const raw = first.parts
    .map((part) => (part.type === "text" && !part.strong && !part.em ? part.text : part.type === "text" ? `**${part.text}**` : ""))
    .join("");
  const match = raw.match(/^\s*(?:\d+\.\s*)?(?:\*\*|__)(.+?)(?:\*\*|__)\s*/);
  if (!match) return block;
  const productName = match[1].replace(/\*/g, "").trim();
  if (productName.split(/\s+/).length < 2) return block;
  const current = block.name.trim();
  if (current.split(/\s+/).length > 1 && current.length >= productName.length * 0.6) {
    return block;
  }
  const rest = raw.slice(match[0].length).trim();
  const body = block.body.filter((item) => item !== first);
  if (rest) body.unshift({ type: "paragraph", parts: splitInline(rest) });
  const title = block.numbered ? `${block.index}. ${productName}` : productName;
  return { ...block, title, name: productName, alt: productName, body };
}

/**
 * Parse a plain-text blog body that contains a markdown product image and/or
 * a shop link on its own line. Returns null when the body should stay as-is.
 */
function markdownFromBody(body: string): string {
  const raw = String(body || "").replace(/\r\n/g, "\n");
  if (!looksLikeHtml(raw)) return raw;
  const stripped = raw
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  if (/!\[[^\]]*\]\(/.test(stripped) || /^\[[^\]]+\]\(\s*<?https?:\/\//m.test(stripped)) {
    return stripped;
  }
  return "";
}

export function parseBlogProductBody(
  body: string,
  options?: { fallbackImage?: string },
): BlogBodyBlock[] | null {
  const raw = markdownFromBody(body);
  if (!raw.trim()) return null;
  const hasImage = /^!\[([^\]]*)\]\(/m.test(raw);
  const hasShopLine = /^\[([^\]]+)\]\(\s*<?https?:\/\//m.test(raw);
  if (!hasImage && !hasShopLine) return null;

  const lines = raw.split("\n");
  const blocks: BlogBodyBlock[] = [];
  let para: string[] = [];
  let lastHeading = "";
  const fallbackImage = usableProductUrl(options?.fallbackImage || "");

  const flushPara = () => {
    const text = para.join(" ").replace(/\s+/g, " ").trim();
    para = [];
    if (!text) return;
    const parts = splitInline(text);
    if (parts.length) blocks.push({ type: "paragraph", parts });
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) {
      flushPara();
      continue;
    }

    const bullet = line.match(BULLET_LINE);
    if (bullet) {
      flushPara();
      const items = [bullet[1].trim()];
      while (i + 1 < lines.length) {
        const nextBullet = lines[i + 1].trim().match(BULLET_LINE);
        if (!nextBullet) break;
        items.push(nextBullet[1].trim());
        i += 1;
      }
      blocks.push({ type: "list", items });
      continue;
    }

    const numbered = line.match(/^\d+[.)]\s+(.*)$/);
    const contentLine = numbered ? numbered[1].trim() : line;
    const image = contentLine.match(IMAGE_LINE);
    if (image) {
      flushPara();
      const role = parseImageRole(image[1]);
      let cursor = i + 1;
      if (cursor < lines.length && !lines[cursor].trim()) cursor += 1;
      // Explicit italic / parenthetical line under the image only — never invent
      // a caption from the markdown alt (e.g. "content: Title").
      let caption = "";
      const captionLine = readCaptionLine(lines[cursor] || "");
      if (captionLine) {
        caption = captionLine;
        cursor += 1;
        if (cursor < lines.length && !lines[cursor].trim()) cursor += 1;
      } else if (role.caption && role.role === "wide") {
        // Only invent a caption from wide: alts — product: Name is the product title, not a photo caption.
        caption = role.caption;
      }
      const link = (lines[cursor] || "").trim().match(LINK_LINE);
      let linkLabel = "";
      let linkHref = "";
      if (link) {
        linkLabel = link[1].trim();
        linkHref = usableProductUrl(link[2]);
        cursor += 1;
      }
      i = cursor - 1;
      const fromMarkdown = usableProductUrl(image[2]);
      const contentTitle = image[1].trim().match(/^content\s*:\s*(.*)$/i);
      if (
        contentTitle ||
        (linkHref && looksLikeContentCard(image[1], linkLabel, linkHref))
      ) {
        const title = displayContentTitle(
          (contentTitle?.[1] || "").trim() ||
            linkLabel.replace(/^content\s*:\s*/i, "").trim() ||
            image[1].trim(),
        );
        blocks.push({
          type: "content",
          image: fromMarkdown,
          title,
          href: linkHref,
          // Content cards: only a real caption line — never the title/alt.
          caption: captionLine || "",
        });
        continue;
      }
      const alt = productAlt(image[1], lastHeading);
      const productShot =
        role.role === "product" ||
        /^(product|image|photo|screenshot)$/i.test(image[1].trim()) ||
        Boolean(numbered);
      const wide = role.role === "wide" || (!linkHref && !productShot);
      const imageUrl = fromMarkdown || (!wide && linkHref ? fallbackImage : "");
      if (wide) {
        if (imageUrl) {
          blocks.push({
            type: "figure",
            image: imageUrl,
            caption: displayCaption(caption),
            span: "full",
          });
        }
        if (linkHref) {
          blocks.push({
            type: "product",
            index: 0,
            numbered: false,
            layout: "side",
            image: "",
            alt,
            caption: "",
            label: linkLabel || `Shop ${alt}`,
            href: linkHref,
            title: "",
            name: alt,
            body: [],
          });
        }
        continue;
      }
      if (!imageUrl && !linkHref) continue;
      blocks.push({
        type: "product",
        index: 0,
        numbered: false,
        layout: "side",
        image: imageUrl,
        alt,
        caption: displayCaption(caption),
        label: linkLabel || `Shop ${alt}`,
        href: linkHref,
        title: "",
        name: alt,
        body: [],
      });
      continue;
    }

    const numberedHeading = line.match(/^(\d+)[.)]\s+(\S.*)$/);
    if (numberedHeading) {
      flushPara();
      lastHeading = plainInlineMarkdown(
        `${numberedHeading[1]}. ${numberedHeading[2].trim()}`,
      );
      blocks.push({ type: "heading", text: lastHeading });
      continue;
    }

    const heading = line.match(HEADING_LINE);
    if (heading) {
      flushPara();
      lastHeading = plainInlineMarkdown(heading[1].trim());
      blocks.push({ type: "heading", text: lastHeading });
      continue;
    }

    const onlyLink = line.match(LINK_LINE);
    if (onlyLink) {
      flushPara();
      const href = usableProductUrl(onlyLink[2]);
      if (!href) {
        para.push(line);
        continue;
      }
      if (
        /^content\s*:/i.test(onlyLink[1]) ||
        looksLikeContentCard("", onlyLink[1], href)
      ) {
        blocks.push({
          type: "content",
          image: "",
          caption: "",
          title: displayContentTitle(
            onlyLink[1].replace(/^content\s*:\s*/i, "").trim(),
          ),
          href,
        });
        continue;
      }
      const alt = productAlt(onlyLink[1], lastHeading);
      blocks.push({
        type: "product",
        index: 0,
        numbered: false,
        layout: "side",
        image: fallbackImage,
        caption: "",
        alt,
        label: onlyLink[1].trim(),
        href,
        title: "",
        name: alt,
        body: [],
      });
      continue;
    }

    para.push(line);
  }
  flushPara();

  if (!blocks.some((block) => isLaidOutBlock(block))) return null;
  return foldProductLooks(blocks);
}

function partsToHtml(parts: BlogBodyPart[]): string {
  return parts
    .map((part) =>
      part.type === "link"
        ? `<a href="${escapeHtml(part.href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(part.text)}</a>`
        : part.strong
          ? `<strong>${escapeHtml(part.text)}</strong>`
          : part.em
            ? `<em>${escapeHtml(part.text)}</em>`
            : escapeHtml(part.text),
    )
    .join("");
}

function copyToHtml(block: BlogBodyCopy): string {
  if (block.type === "list") {
    return `<ul class="mte-tpl-post-list">${block.items
      .map((item) => `<li>${escapeHtml(item)}</li>`)
      .join("")}</ul>`;
  }
  return `<p>${partsToHtml(block.parts)}</p>`;
}

function productLookHtml(block: Extract<BlogBodyBlock, { type: "product" }>): string {
  const wide = block.layout === "full";
  const num = block.numbered
    ? `<p class="mte-tpl-post-look-num">${String(block.index).padStart(2, "0")}</p>`
    : "";
  const img = block.image
    ? `<img class="mte-tpl-post-look-img" src="${escapeHtml(block.image)}" alt="${escapeHtml(block.caption || block.alt)}" />`
    : "";
  const media = img
    ? block.href
      ? `<a class="mte-tpl-post-look-photo" href="${escapeHtml(block.href)}" target="_blank" rel="noopener noreferrer">${img}</a>`
      : img
    : "";
  const productName = cleanProductLabel(block.name) || cleanProductLabel(block.alt);
  const lookCaption = displayCaption(cleanProductLabel(block.caption));
  // Don't repeat the product name as an italic caption under the photo.
  const cap =
    lookCaption &&
    lookCaption.toLowerCase() !== productName.toLowerCase()
      ? `<p class="mte-tpl-post-look-caption">${escapeHtml(lookCaption)}</p>`
      : "";
  const titleIsName =
    cleanProductLabel(block.title).toLowerCase() === productName.toLowerCase();
  const copy = block.body.map(copyToHtml).join("");
  const articleClass = wide ? "mte-tpl-post-look mte-tpl-post-look--full" : "mte-tpl-post-look";
  // Product name is the shop CTA — clickable when we have a shop link.
  const name =
    wide || titleIsName || !productName
      ? ""
      : block.href
        ? `<a class="mte-tpl-post-look-name mte-tpl-post-look-name--link" href="${escapeHtml(block.href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(productName)}</a>`
        : `<p class="mte-tpl-post-look-name">${escapeHtml(productName)}</p>`;
  const title = block.title
    ? `<h3 class="mte-tpl-post-look-title">${escapeHtml(block.title)}</h3>`
    : "";
  return `<article class="${articleClass}"><div class="mte-tpl-post-look-media">${media}${cap}${num}${name}</div><div class="mte-tpl-post-look-copy">${title}${copy}</div></article>`;
}


function blockToHtml(block: BlogBodyBlock): string {
  if (block.type === "heading") {
    return `<h3 class="mte-tpl-post-h3">${escapeHtml(block.text)}</h3>`;
  }
  if (block.type === "content") return contentCardHtml(block);
  if (block.type === "figure") {
    const caption = displayCaption(block.caption);
    const cap = caption ? `<figcaption>${escapeHtml(caption)}</figcaption>` : "";
    return `<figure class="mte-tpl-post-figure mte-tpl-post-figure--full"><img src="${escapeHtml(block.image)}" alt="${escapeHtml(caption)}" />${cap}</figure>`;
  }
  if (block.type === "list") return copyToHtml(block);
  if (block.type === "product") return productLookHtml(block);
  return `<p>${partsToHtml(block.parts)}</p>`;
}

type GroupedBlogBlock =
  | BlogBodyBlock
  | { type: "content-gallery"; items: Extract<BlogBodyBlock, { type: "content" }>[] }
  | { type: "figure-gallery"; items: Extract<BlogBodyBlock, { type: "figure" }>[] };

/** Collapse consecutive content cards / leftover figures into magazine galleries. */
function groupBlocksForRender(blocks: BlogBodyBlock[]): GroupedBlogBlock[] {
  const out: GroupedBlogBlock[] = [];
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    if (block.type === "content") {
      const items: Extract<BlogBodyBlock, { type: "content" }>[] = [block];
      while (i + 1 < blocks.length && blocks[i + 1].type === "content") {
        i += 1;
        items.push(blocks[i] as Extract<BlogBodyBlock, { type: "content" }>);
      }
      out.push({ type: "content-gallery", items });
      continue;
    }
    if (block.type === "figure") {
      const items: Extract<BlogBodyBlock, { type: "figure" }>[] = [block];
      while (i + 1 < blocks.length && blocks[i + 1].type === "figure") {
        i += 1;
        items.push(blocks[i] as Extract<BlogBodyBlock, { type: "figure" }>);
      }
      // One figure between sections stays full-width; 2+ extras grid like content.
      if (items.length === 1) out.push(items[0]);
      else out.push({ type: "figure-gallery", items });
      continue;
    }
    out.push(block);
  }
  return out;
}

export function blogHasContentRoundup(blocks: BlogBodyBlock[] | null | undefined): boolean {
  return Boolean(blocks?.some((block) => block.type === "content"));
}

export function blogMarkdownBodyToHtml(
  body: string,
  options?: { fallbackImage?: string },
): string | null {
  // Studio embeds <!--blog-editor-state:…--> for draft reload — never render it.
  const cleaned = String(body || "")
    .replace(/<!--blog-editor-state:[\s\S]*?-->\s*$/g, "")
    .trim();
  const blocks = parseBlogProductBody(cleaned, options);
  if (!blocks) return null;
  const lastProduct = blocks.reduce(
    (at, block, index) => (isLaidOutBlock(block) ? index : at),
    -1,
  );
  const grouped = groupBlocksForRender(blocks);
  const html = grouped.map((block) => {
    if (block.type === "content-gallery") return contentGalleryHtml(block.items);
    if (block.type === "figure-gallery") return figureGalleryHtml(block.items);
    return blockToHtml(block);
  });
  const isRoundup = blogHasContentRoundup(blocks);
  const colClass = isRoundup
    ? "mte-tpl-post-products-col mte-tpl-post-products-col--roundup"
    : "mte-tpl-post-products-col";
  if (lastProduct < 0) {
    return isRoundup
      ? `<div class="${colClass}">${html.join("")}</div>`
      : html.join("");
  }
  // Rebuild with original slicing using grouped HTML is awkward; keep flat join
  // inside the column when any laid-out block exists.
  return `<div class="${colClass}">${html.join("")}</div>`;
}

export function BlogProductBody({
  blocks,
  className,
}: {
  blocks: BlogBodyBlock[];
  className?: string;
}) {
  const { editable } = useEdit();

  const holdInEditor = editable
    ? (e: React.MouseEvent) => {
        e.preventDefault();
      }
    : undefined;

  const renderParts = (parts: BlogBodyPart[], keyPrefix: string) =>
    parts.map((part, partIndex) =>
      part.type === "link" ? (
        <a
          key={`${keyPrefix}-${partIndex}`}
          href={part.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={holdInEditor}
        >
          {part.text}
        </a>
      ) : part.strong ? (
        <strong key={`${keyPrefix}-${partIndex}`}>{part.text}</strong>
      ) : part.em ? (
        <em key={`${keyPrefix}-${partIndex}`}>{part.text}</em>
      ) : (
        <React.Fragment key={`${keyPrefix}-${partIndex}`}>{part.text}</React.Fragment>
      ),
    );

  const isRoundup = blogHasContentRoundup(blocks);
  const renderContentCard = (
    block: Extract<BlogBodyBlock, { type: "content" }>,
    index: number,
  ) => {
    const title = displayContentTitle(block.title);
    const caption = displayCaption(block.caption);
    const hasMeta = Boolean(title || caption);
    const media = (
      <>
        {block.image ? (
          <div className="mte-tpl-post-content-media">
            <img src={block.image} alt={title || "Content"} />
          </div>
        ) : null}
        {hasMeta ? (
          <div className="mte-tpl-post-content-body">
            {title ? (
              <span className="mte-tpl-post-content-title">{title}</span>
            ) : null}
            {caption ? (
              <p className="mte-tpl-post-content-caption">{caption}</p>
            ) : null}
          </div>
        ) : null}
      </>
    );
    return (
      <article
        key={`content-${index}`}
        className={[
          "mte-tpl-post-content",
          hasMeta ? "" : "mte-tpl-post-content--media-only",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {block.href ? (
          <a
            href={block.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={holdInEditor}
          >
            {media}
          </a>
        ) : (
          media
        )}
      </article>
    );
  };

  const renderFigure = (
    block: Extract<BlogBodyBlock, { type: "figure" }>,
    key: string,
    grid: boolean,
  ) => {
    const caption = displayCaption(block.caption);
    return (
      <figure
        key={key}
        className={
          grid
            ? "mte-tpl-post-figure mte-tpl-post-figure--grid"
            : "mte-tpl-post-figure mte-tpl-post-figure--full"
        }
      >
        <img src={block.image} alt={caption || ""} />
        {caption ? <figcaption>{caption}</figcaption> : null}
      </figure>
    );
  };

  const renderBlock = (block: GroupedBlogBlock, index: number) => {
        if (block.type === "content-gallery") {
          return (
            <div
              key={`gallery-${index}`}
              className="mte-tpl-post-content-gallery"
              data-count={block.items.length}
            >
              {block.items.map((item, itemIndex) =>
                renderContentCard(item, itemIndex),
              )}
            </div>
          );
        }
        if (block.type === "figure-gallery") {
          return (
            <div
              key={`figures-${index}`}
              className="mte-tpl-post-figure-gallery mte-tpl-post-content-gallery"
              data-count={block.items.length}
            >
              {block.items.map((item, itemIndex) =>
                renderFigure(item, `fig-${index}-${itemIndex}`, true),
              )}
            </div>
          );
        }
        if (block.type === "heading") {
          return (
            <h3 key={index} className="mte-tpl-post-h3">
              {block.text}
            </h3>
          );
        }
        if (block.type === "list") {
          return (
            <ul key={index} className="mte-tpl-post-list">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{item}</li>
              ))}
            </ul>
          );
        }
        if (block.type === "figure") {
          return renderFigure(block, `figure-${index}`, false);
        }
        if (block.type === "content") {
          return renderContentCard(block, index);
        }
        if (block.type === "product") {
          const wide = block.layout === "full";
          const productName =
            cleanProductLabel(block.name) || cleanProductLabel(block.alt);
          const heading =
            cleanProductLabel(block.title) ||
            (block.numbered && productName
              ? `${block.index}. ${productName}`
              : productName);
          const titleIsName =
            cleanProductLabel(block.title).toLowerCase() ===
            productName.toLowerCase();
          const lookCaption = displayCaption(cleanProductLabel(block.caption));
          const showCaption =
            Boolean(lookCaption) &&
            lookCaption.toLowerCase() !== productName.toLowerCase();
          const photo = block.image ? (
            <img
              className="mte-tpl-post-look-img"
              src={block.image}
              alt={productName || lookCaption || ""}
            />
          ) : null;
          const nameEl =
            !wide && productName && !titleIsName ? (
              block.href ? (
                <a
                  className="mte-tpl-post-look-name mte-tpl-post-look-name--link"
                  href={block.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={holdInEditor}
                >
                  {productName}
                </a>
              ) : (
                <p className="mte-tpl-post-look-name">{productName}</p>
              )
            ) : null;
          return (
            <article
              key={index}
              className={[
                "mte-tpl-post-look",
                wide ? "mte-tpl-post-look--full" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <div className="mte-tpl-post-look-media">
                {photo && block.href ? (
                  <a
                    className="mte-tpl-post-look-photo"
                    href={block.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={holdInEditor}
                  >
                    {photo}
                  </a>
                ) : (
                  photo
                )}
                {showCaption ? (
                  <p className="mte-tpl-post-look-caption">{lookCaption}</p>
                ) : null}
                {!wide && block.numbered ? (
                  <p className="mte-tpl-post-look-num">{String(block.index).padStart(2, "0")}</p>
                ) : null}
                {nameEl}
              </div>
              {wide ? null : (
              <div className="mte-tpl-post-look-copy">
                {heading && !titleIsName ? (
                  <h3 className="mte-tpl-post-look-title">{heading}</h3>
                ) : null}
                {block.body.map((item, paraIndex) =>
                  item.type === "list" ? (
                    <ul key={paraIndex} className="mte-tpl-post-list">
                      {item.items.map((entry, itemIndex) => (
                        <li key={itemIndex}>{entry}</li>
                      ))}
                    </ul>
                  ) : (
                    <p key={paraIndex}>{renderParts(item.parts, String(paraIndex))}</p>
                  ),
                )}
              </div>
              )}
            </article>
          );
        }
        return (
          <p key={index} className="mte-tpl-post-body-p">
            {renderParts(block.parts, String(index))}
          </p>
        );
  };

  const grouped = groupBlocksForRender(blocks);
  const rendered = grouped.map(renderBlock);
  const bodyClass = [
    className,
    isRoundup ? "mte-tpl-post-body--content-roundup" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={bodyClass}>
      <div
        className={[
          "mte-tpl-post-products-col",
          isRoundup ? "mte-tpl-post-products-col--roundup" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {rendered}
      </div>
    </div>
  );
}
